import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, FileCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createDocument, updateDocument, type DocumentDTO } from '@/services/documents';
import { listTemplates } from '@/services/templates';
import {
  clinicalDocumentLabel,
  templateTypesForClinicalUi,
  toClinicalDocumentApiType,
  type ClinicalDocumentUiType,
} from '@/lib/clinicalDocuments';
import { TherapeuticSuggestionPanel } from './TherapeuticSuggestionPanel';
import {
  CLINICAL_SUGGESTION_DISCLAIMER,
  mergeSuggestionIntoContent,
} from '@/lib/therapeuticSuggestion';

interface DocumentEditorProps {
  appointmentId: string;
  patientId: string;
  documentType: ClinicalDocumentUiType;
  existingDocument?: DocumentDTO | null;
  onClose: () => void;
  onSuccess: (documentId?: string) => void;
}

function isPrescriptionType(documentType: ClinicalDocumentUiType): boolean {
  return documentType === 'PRESCRIPTION' || documentType === 'RECIPE';
}

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  appointmentId,
  patientId: _patientId,
  documentType,
  existingDocument = null,
  onClose,
  onSuccess,
}) => {
  const isEdit = Boolean(existingDocument?.id);
  const showTherapeuticAssist = isPrescriptionType(documentType) && !isEdit;
  const [content, setContent] = useState(existingDocument?.content || '');
  const [templates, setTemplates] = useState<
    Array<{ id: string; name: string; content: string; type: string }>
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [usedSuggestion, setUsedSuggestion] = useState(false);
  const [physicianConfirmed, setPhysicianConfirmed] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (existingDocument?.content) {
      setContent(existingDocument.content);
    }
  }, [existingDocument?.id, existingDocument?.content]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true);
        const res = await listTemplates();
        if (res.success && res.data) {
          const allowed = new Set(templateTypesForClinicalUi(documentType));
          const filtered = res.data.filter((t) => allowed.has(t.type));
          setTemplates(filtered.length > 0 ? filtered : res.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    if (!isEdit) fetchTemplates();
  }, [documentType, isEdit]);

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    if (id) {
      const template = templates.find((t) => t.id === id);
      if (template) setContent(template.content);
    } else {
      setContent('');
    }
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    if (!appointmentId && !isEdit) {
      toast.error('Consulta inválida para emitir documento');
      return;
    }
    if (!content || content.trim() === '<p><br></p>') {
      toast.error('O conteúdo do documento não pode estar vazio');
      return;
    }

    if (showTherapeuticAssist && usedSuggestion && !physicianConfirmed) {
      toast.error(
        'Confirme que revisou o conteúdo assistido antes de emitir a receita',
      );
      return;
    }

    // Confirmação explícita também para receita manual (responsabilidade clínica)
    if (showTherapeuticAssist && !physicianConfirmed) {
      toast.error('Confirme a revisão médica antes de emitir a receita');
      return;
    }

    const contentToSave =
      showTherapeuticAssist && physicianConfirmed
        ? appendReviewMarker(content)
        : content;

    try {
      savingRef.current = true;
      setIsSaving(true);

      if (isEdit && existingDocument?.id) {
        const response = await updateDocument(existingDocument.id, {
          content: contentToSave,
        });
        if (response.success && response.data?.id) {
          toast.success('Documento atualizado com sucesso!');
          onSuccess(response.data.id);
        } else {
          toast.error(response.error || 'Erro ao atualizar documento');
        }
        return;
      }

      const response = await createDocument({
        appointmentId,
        documentType: toClinicalDocumentApiType(documentType),
        content: contentToSave,
      });

      if (response.success && response.data?.id) {
        toast.success('Documento emitido com sucesso!');
        onSuccess(response.data.id);
      } else {
        toast.error(response.error || 'Erro ao salvar documento');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro de conexão ao salvar documento');
    } finally {
      setIsSaving(false);
      savingRef.current = false;
    }
  };

  const title = clinicalDocumentLabel(toClinicalDocumentApiType(documentType));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-8">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        <CardHeader className="flex flex-row items-center justify-between border-b pb-4 shrink-0 bg-neutral-50 dark:bg-neutral-900">
          <CardTitle className="text-xl">
            {isEdit ? `Editar ${title}` : `Novo ${title}`}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSaving}>
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto p-4 flex flex-col gap-4">
          {!isEdit && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Usar Modelo/Template</label>
              <div className="flex gap-2 items-center">
                <select
                  value={selectedTemplateId}
                  onChange={handleTemplateChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={isLoadingTemplates || isSaving}
                >
                  <option value="">-- Documento em branco --</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({clinicalDocumentLabel(t.type)})
                    </option>
                  ))}
                </select>
                {isLoadingTemplates && (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          )}

          {showTherapeuticAssist && (
            <TherapeuticSuggestionPanel
              disabled={isSaving}
              onApply={(_payload, html) => {
                setContent((prev) => mergeSuggestionIntoContent(prev, html));
                setUsedSuggestion(true);
                setPhysicianConfirmed(false);
                toast.success(
                  'Sugestão aplicada. Revise e edite livremente antes de emitir.',
                );
              }}
            />
          )}

          <div className="flex-1 min-h-[300px] border rounded-md overflow-hidden">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              placeholder="Digite o conteúdo do documento..."
              className="h-full"
              readOnly={isSaving}
            />
          </div>

          {showTherapeuticAssist && (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={physicianConfirmed}
                onChange={(e) => setPhysicianConfirmed(e.target.checked)}
                disabled={isSaving}
              />
              <span>
                Confirmo que revisei o conteúdo e que a responsabilidade clínica
                desta receita é minha.{' '}
                <em className="text-muted-foreground">
                  {CLINICAL_SUGGESTION_DISCLAIMER}
                </em>
              </span>
            </label>
          )}
        </CardContent>

        <CardFooter className="border-t p-4 flex justify-between shrink-0 bg-neutral-50 dark:bg-neutral-900">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || (showTherapeuticAssist && !physicianConfirmed)}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <FileCheck className="w-4 h-4 mr-2" />
            )}
            {isEdit ? 'Salvar alterações' : 'Emitir Documento'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

function appendReviewMarker(content: string): string {
  const marker =
    '<p><em>Conteúdo revisado e confirmado pelo médico responsável.</em></p>';
  if (content.includes('revisado e confirmado pelo médico')) {
    return content;
  }
  return `${content}${marker}`;
}
